/**
 * One Settings area for the whole site (18 Oct 2026), inside the shell: People and access for every admin, then each
 * app's own settings for that app's admins (Attendance: uploads, HR data, accounts, Tier 1, rules; Click Up; Task
 * Force), then usage and activity. Replaces Admin and Task Force's own settings page; their old addresses lead here.
 */
import { useEffect } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { AppShell } from '../components/AppChrome';
import { useMe } from '../lib/session';
import type { Access } from '../lib/api';
import { useStackedTableLabels } from '../admin/ui';
import { installFloatingHeaders } from '../report/floatingHeaders';
import { Icon } from '../taskforce/ui';
import '../styles/report.css';
import '../styles/app.css';
import '../styles/admin.css';

type Who = 'any' | 'attendance' | 'clickup' | 'taskforce';
export const SECTIONS: Array<{ path: string; label: string; icon: string; group: string; who: Who }> = [
  { path: 'people', label: 'People and access', icon: 'users', group: 'People', who: 'any' },
  { path: 'uploads', label: 'Uploads', icon: 'upload', group: 'Attendance', who: 'attendance' },
  { path: 'weekly', label: 'Weekly emails', icon: 'mail', group: 'Attendance', who: 'attendance' },
  { path: 'data', label: 'HR data', icon: 'table', group: 'Attendance', who: 'attendance' },
  { path: 'accounts', label: 'Accounts and leads', icon: 'user', group: 'Attendance', who: 'attendance' },
  { path: 'tier1', label: 'Tier 1', icon: 'flag', group: 'Attendance', who: 'attendance' },
  { path: 'attendance', label: 'Attendance rules', icon: 'clock', group: 'Attendance', who: 'attendance' },
  { path: 'clickup', label: 'Click Up', icon: 'chart', group: 'Click Up', who: 'clickup' },
  { path: 'taskforce', label: 'Task Force', icon: 'home', group: 'Task Force', who: 'taskforce' },
  { path: 'usage', label: 'Utilization', icon: 'chart', group: 'Insights', who: 'attendance' },
  { path: 'activity', label: 'Activity', icon: 'list', group: 'Insights', who: 'attendance' },
];

export const allowedSections = (a: Access | undefined) =>
  SECTIONS.filter((s) => (s.who === 'any' ? !!a?.settings : !!a?.admin[s.who]));

export function SettingsLayout() {
  useEffect(() => installFloatingHeaders(), []);
  useStackedTableLabels();
  const me = useMe();
  const location = useLocation();
  const sections = allowedSections(me.data?.access);
  const here = location.pathname.split('/')[2] ?? '';
  // /settings, or a section they do not manage: their first one.
  if (sections.length && !sections.some((s) => s.path === here))
    return <Navigate to={`/settings/${sections[0]!.path}`} replace />;
  const groups = [...new Set(sections.map((s) => s.group))];
  return (
    <AppShell area="settings">
      <div className="setlayout">
        <nav className="setnav" aria-label="Settings sections">
          <h1 id="title">Settings</h1>
          {groups.map((g) => (
            <div key={g} className="setnav-g">
              <span className="setnav-h">{g}</span>
              {sections
                .filter((s) => s.group === g)
                .map((s) => (
                  <NavLink key={s.path} to={`/settings/${s.path}`}>
                    <Icon n={s.icon} size={15} />
                    {s.label}
                  </NavLink>
                ))}
            </div>
          ))}
        </nav>
        <div className="setbody wrap adminroot">
          <Outlet />
        </div>
      </div>
    </AppShell>
  );
}

/** Old addresses (/admin/…, /taskforce/-/settings) lead to the same place here. */
const OLD: Record<string, string> = {
  uploads: 'uploads',
  data: 'data',
  access: 'people',
  tier1: 'tier1',
  settings: 'attendance',
  clickup: 'clickup',
  usage: 'usage',
  audit: 'activity',
};
export function OldAdmin() {
  const location = useLocation();
  const part = location.pathname.split('/')[2] ?? '';
  return <Navigate to={`/settings/${OLD[part] ?? ''}${location.search}`} replace />;
}
